import React from 'react'

export default function Conundrum() {
  return (
      <div className="max-w-4xl mx-auto px-6 py-10 text-gray-800 leading-relaxed">
      
      {/* Title */}
      <h1 className="text-3xl font-bold mb-6">
        The Article 84 Conundrum: Finality, Fairness and the Future of African Football
      </h1>

      {/* Introduction */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Introduction</h2>
        <p className="mb-4">
          Few moments in African football have captured the tension between drama and law quite like
          the explosive AFCON (African Cup of Nations) final between Senegal and Morocco. What unfolded
          was not merely a contest for continental supremacy, but a spectacle of protest, interruption,
          and eventual triumph only for the result to be dramatically reversed in a boardroom.
        </p>
        <p>
          The Senegalese players walked off, only to later return, and the match was ultimately completed.
          The expectation was that the final whistle had settled matters. Yet the subsequent decision by
          CAF to invoke its disciplinary powers and declare a forfeiture has transformed a football match
          into a legal battleground.<sup className="text-blue-600">1</sup>
        </p>
      </section>

      {/* Articles Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">
          Articles 82, 83 and 84 of the Regulations
        </h2>

        <p className="mb-4">
          At its core, Article 84 empowers CAF to impose sanctions, including forfeiture, where misconduct
          such as walking off, refusing to play, leaving before the end without the referee’s permission,
          or failing to show up within fifteen minutes of kick-off are established.<sup className="text-blue-600">1</sup>
        </p>

        <p className="mb-4">
          When read together with Articles 82<sup className="text-blue-600">2</sup> and 83<sup className="text-blue-600">3</sup>,
          and alongside the IFAB Laws of the Game, a legal conundrum arises:
        </p>

        {/* Highlighted Quote */}
        <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 my-4">
          “Does CAF’s power to impose forfeiture extend to matches already completed under the referee’s authority?”
        </blockquote>

        <p>
          The difficulty lies in the division of authority. Under Law 5 of the IFAB framework, the referee
          is the ultimate authority on the field of play, and decisions on match facts are final.<sup className="text-blue-600">4</sup>
        </p>
      </section>

      {/* Legal Analysis */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Legal Analysis</h2>

        <p className="mb-4">
          This principle is reinforced by the Field of Play Doctrine, consistently upheld by the Court of
          Arbitration for Sport (CAS), which limits post-match interference except in cases of bad faith,
          corruption, or clear misapplication of the rules.<sup className="text-blue-600">5</sup>
        </p>

        <p className="mb-4">
          In the present context, the referee allowed the match to proceed to completion. That decision is
          not merely procedural — it is a factual determination that the conduct in question did not warrant
          abandonment. To later characterise the same conduct as justifying forfeiture creates a conundrum:
          the match is treated as both validly completed and retrospectively nullified.
        </p>

        <p>
          This raises serious concerns for legal certainty. If governing bodies are permitted to reclassify
          completed matches as forfeited, the authority of referees is undermined, and the predictability of
          competition outcomes is compromised.
        </p>
      </section>

      {/* CAS Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Role of CAS</h2>

        <p className="mb-4">
          CAS, as the supreme arbitral body in international sport, has jurisdiction to review CAF decisions.
          While it does not revisit the referee’s factual findings, it can examine whether CAF acted within
          its regulatory powers and applied its rules correctly.
        </p>

        <p>
          If CAF’s decision is found to be legally inconsistent, disproportionate, or unsupported by its own
          regulations, CAS is empowered to set it aside.
        </p>
      </section>

      {/* Conclusion */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2">Conclusion</h2>

        <p className="mb-4">
          The solution lies in clarity. CAF must ensure that its regulations expressly define when forfeiture
          may be imposed after a match and in what circumstances referee decisions may be revisited.
        </p>

        <p>
          Where a match is completed under the authority of the referee, its result ought to stand, with
          misconduct addressed through disciplinary sanctions rather than retrospective alteration of the
          scoreline.
        </p>

        <p className="mt-4 font-semibold">
          In short, Article 84 is a powerful tool — but if applied without restraint, it risks unsettling the
          very foundation of the game it seeks to protect.
        </p>
      </section>

      {/* Authors */}
      <footer className="border-t pt-4 mt-10 text-sm text-gray-600">
        <p>
          <strong>By:</strong> D Matawu (Partner), assisted by T D Muchatibaya (Associate) & D T Dhumbura
        </p>
      </footer>

      {/* References */}
      <section className="mt-10">
        <h2 className="text-lg font-semibold mb-4">References</h2>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
          <li>
            Article 84 of CAF Regulations – “The team which contravenes the provisions of Articles 82 and 83
            shall be eliminated… and lose its match by 3-0 unless a more advantageous result exists.”
          </li>
          <li>
            Article 82 of CAF Regulations – Withdrawal, refusal to play, or leaving before the end without
            referee authorization results in elimination.
          </li>
          <li>
            Article 83 of CAF Regulations – Failure to appear within 15 minutes of kick-off results in forfeiture.
          </li>
          <li>
            IFAB Laws of the Game, Law 5 – The Referee. Available at:
            <a
              href="https://www.theifab.com/laws/latest/the-referee/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 ml-1 underline"
            >
              theifab.com
            </a>
          </li>
          <li>
            Jonathan Mason, “Skating on Thin Ice: The CAS re-affirms the Field of Play Doctrine in the Kyiv Capitals case” (2025). Available at:
            <a
              href="https://www.sports.legal/2025/04/skating-on-thin-ice-the-court-of-arbitration-for-sport-re-affirms-the-field-of-play-doctrine-in-the-hockey-club-kyiv-capitals-case/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 ml-1 underline"
            >
              sports.legal
            </a>
          </li>
        </ol>
      </section>
    </div>
  )
}
